// frontend/src/pages/StatisticsPage.tsx
import React, { useEffect, useState } from 'react';
import { useApi } from '../hooks/useApi';
import { 
  Box, 
  Paper, 
  Typography, 
  Alert, 
  styled, 
  Card,
  CardContent,
  useTheme,
  alpha,
  Fade,
  Skeleton,
  Divider,
  Chip
} from '@mui/material';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
    PieChart, Pie, Cell
} from 'recharts';
import { useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AssessmentIcon from '@mui/icons-material/Assessment';
import SecurityIcon from '@mui/icons-material/Security';
import DescriptionIcon from '@mui/icons-material/Description';
import BugReportIcon from '@mui/icons-material/BugReport';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ShieldIcon from '@mui/icons-material/Shield';
import RssFeedIcon from '@mui/icons-material/RssFeed';
import { cyber } from '../theme';

// Custom styled components
const StatsContainer = styled(Box)(({ theme }) => ({
  display: 'grid',
  gap: theme.spacing(3),
  marginBottom: theme.spacing(4),
  gridTemplateColumns: 'repeat(1, 1fr)',
  [theme.breakpoints.up('sm')]: {
    gridTemplateColumns: 'repeat(2, 1fr)',
  },
  [theme.breakpoints.up('md')]: {
    gridTemplateColumns: 'repeat(4, 1fr)',
  },
}));

const ChartContainer = styled(Box)(({ theme }) => ({
  display: 'grid',
  gap: theme.spacing(3),
  gridTemplateColumns: '1fr',
  [theme.breakpoints.up('lg')]: {
    gridTemplateColumns: 'repeat(2, 1fr)',
  },
}));

const GradientCard = styled(Card)(() => ({
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-3px)',
    boxShadow: cyber.glow,
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: cyber.gradient,
  }
}));

const ChartPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  height: '100%',
  border: `1px solid ${cyber.border}`,
  transition: 'border-color 0.25s ease, box-shadow 0.25s ease',
  '&:hover': {
    borderColor: alpha(cyber.primary, 0.6),
    boxShadow: cyber.glow,
  }
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  marginBottom: theme.spacing(3),
  marginTop: theme.spacing(4),
}));

// CVE Statistics interfaces
interface CVESeverityDistribution {
  name: string;  // Backend uses "name" not "severity"
  value: number; // Backend uses "value" not "count"
}

interface CVEVendorStat {
  vendor: string;
  count: number;
}

interface CVETopMentioned {
  cve_id: string;
  mention_count: number;
  cvss_v3_severity: string | null;  // Backend uses cvss_v3_severity, not cvss_score + severity
}

interface StatisticsData {
  total_reports: number;
  reports_last_week: number;
  total_unique_sources: number;
  top_report_sources: { source: string; count: number }[];
  top_tags: { name: string; value: number }[];
  total_iocs: number;
  iocs_last_7_days: number;
  ioc_type_distribution: { name: string; value: number }[];
  // CVE Statistics - fields come directly in the response (not nested under cve_statistics)
  total_cves?: number;
  cves_last_7_days?: number;
  cves_last_30_days?: number;
  cve_severity_distribution?: CVESeverityDistribution[];
  top_cve_vendors?: CVEVendorStat[];
  top_mentioned_cves?: CVETopMentioned[];
  avg_cvss_score?: number | null;
  exploited_in_wild_count?: number;
}

interface StatCardProps {
  title: string;
  value: number | string;
  icon: React.ReactNode;
  color: string;
  loading?: boolean;
  subtitle?: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, color, loading, subtitle }) => {
  if (loading) {
    return (
      <GradientCard>
        <CardContent>
          <Skeleton variant="circular" width={48} height={48} />
          <Skeleton variant="text" sx={{ mt: 2 }} />
          <Skeleton variant="rectangular" height={40} sx={{ mt: 1 }} />
        </CardContent>
      </GradientCard>
    );
  }

  return (
    <Fade in timeout={500}>
      <GradientCard>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: alpha(color, 0.1),
                color: color,
              }}
            >
              {icon}
            </Box>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {title}
          </Typography>
          <Typography
            variant="h4"
            fontWeight="bold"
            sx={{ color: color, fontFamily: cyber.fontMono }}
          >
            {typeof value === 'number' ? value.toLocaleString() : value}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </CardContent>
      </GradientCard>
    </Fade>
  );
};

// Enhanced color palette — purple leads, harmonized accents diversify the series
const COLORS = [
  cyber.primary, cyber.accent.cyan, cyber.accent.green, cyber.accent.coral,
  cyber.primaryLight, cyber.accent.amber, '#f093fb', '#30cfd0'
];

// CVE Severity colors - must match backend format (Title Case)
const SEVERITY_COLORS: { [key: string]: string } = {
  'Critical': '#d32f2f',
  'High': '#f57c00',
  'Medium': '#fbc02d',
  'Low': '#388e3c',
  'None': '#9e9e9e',
  'Unknown': '#757575',
  // Also support uppercase for cvss_v3_severity from backend
  'CRITICAL': '#d32f2f',
  'HIGH': '#f57c00',
  'MEDIUM': '#fbc02d',
  'LOW': '#388e3c',
  'NONE': '#9e9e9e',
  'UNKNOWN': '#757575'
};

const RADIAN = Math.PI / 180;
const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }: any) => {
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  if (percent < 0.05) return null; // Hide labels for small slices

  return (
    <text 
      x={x} 
      y={y} 
      fill="white" 
      textAnchor={x > cx ? 'start' : 'end'} 
      dominantBaseline="central"
      style={{ fontWeight: 600, fontSize: '14px' }}
    >
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

// Custom tooltip
const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <Paper sx={{ p: 1.5, boxShadow: 3 }}>
        <Typography variant="body2" fontWeight="bold">
          {label || payload[0].name || payload[0].payload?.severity || payload[0].payload?.cve_id}
        </Typography>
        <Typography variant="body2" sx={{ color: cyber.accent.cyan, fontFamily: cyber.fontMono }}>
          {payload[0].value.toLocaleString()}
        </Typography>
      </Paper>
    );
  }
  return null;
};

// CVE Tooltip with more details
const CVETooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <Paper sx={{ p: 1.5, boxShadow: 3 }}>
        <Typography variant="body2" fontWeight="bold">
          {data.cve_id}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Mentions: {data.mention_count}
        </Typography>
        {data.cvss_v3_severity && (
          <Chip 
            label={data.cvss_v3_severity} 
            size="small" 
            sx={{ 
              mt: 0.5,
              bgcolor: alpha(SEVERITY_COLORS[data.cvss_v3_severity] || '#757575', 0.2),
              color: SEVERITY_COLORS[data.cvss_v3_severity] || '#757575',
              fontWeight: 'bold'
            }} 
          />
        )}
      </Paper>
    );
  }
  return null;
};

const StatisticsPage: React.FC = () => {
  const theme = useTheme();
  const { authenticatedFetcher } = useApi();
  const { inProgress } = useMsal();
  const [stats, setStats] = useState<StatisticsData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (inProgress !== InteractionStatus.None) return;

    const fetchStatistics = async () => {
      try {
        setLoading(true);
        const responseData = await authenticatedFetcher("/api/statistics");
        setStats(responseData);
        setError(null);
      } catch (err: any) {
        setError(err.message || 'Failed to fetch statistics');
      } finally {
        setLoading(false);
      }
    };

    fetchStatistics();
  }, [authenticatedFetcher, inProgress]);

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      </Box>
    );
  }

  // Styled component for hero section
  const HeroSection = styled(Box)(({ theme }) => ({
    background: `linear-gradient(135deg, ${cyber.bgPaper} 0%, #3a2d6e 55%, ${cyber.primary} 130%)`,
    border: `1px solid ${alpha(cyber.primary, 0.35)}`,
    borderRadius: theme.spacing(2),
    padding: theme.spacing(3),
    marginBottom: theme.spacing(4),
    color: 'white',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: '-50%',
      right: '-10%',
      width: '300px',
      height: '300px',
      background: alpha(cyber.primaryLight, 0.25),
      borderRadius: '50%',
      filter: 'blur(80px)',
    }
  }));

  // Transform severity distribution for pie chart (data is already in correct format from backend)
  const severityChartData = stats?.cve_severity_distribution || [];

  // Transform top mentioned CVEs for bar chart
  const topCVEsChartData = stats?.top_mentioned_cves?.slice(0, 10) || [];

  // Transform top vendors for bar chart
  const topVendorsChartData = stats?.top_cve_vendors?.slice(0, 10) || [];

  return (
    <Box sx={{ p: 3 }}>
      {/* Hero Section */}
      <HeroSection>
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <AssessmentIcon sx={{ fontSize: 36 }} />
            <Typography variant="h4" component="h1" fontWeight="bold">
              Platform Analytics
            </Typography>
          </Box>
          <Typography variant="h6" sx={{ opacity: 0.95 }}>
            Real-time insights and performance metrics
          </Typography>
        </Box>
      </HeroSection>

      {/* Reports & IOCs KPI Cards */}
      <SectionHeader>
        <DescriptionIcon color="primary" sx={{ fontSize: 28 }} />
        <Typography variant="h5" fontWeight="bold">Reports & IOCs</Typography>
      </SectionHeader>
      
      <StatsContainer sx={{ gridTemplateColumns: { md: 'repeat(5, 1fr)' } }}>
        <StatCard
          title="Total Reports" 
          value={stats?.total_reports || 0}
          icon={<DescriptionIcon sx={{ fontSize: 28 }} />}
          color={cyber.primary}
          loading={loading}
        />
        <StatCard 
          title="Reports (Last 7 Days)"
          value={stats?.reports_last_week || 0}
          icon={<TrendingUpIcon sx={{ fontSize: 28 }} />}
          color={cyber.accent.green}
          loading={loading}
        />
        <StatCard
          title="Total IOCs"
          value={stats?.total_iocs || 0}
          icon={<SecurityIcon sx={{ fontSize: 28 }} />}
          color={cyber.accent.coral}
          loading={loading}
        />
        <StatCard
          title="IOCs (Last 7 Days)"
          value={stats?.iocs_last_7_days || 0}
          icon={<AssessmentIcon sx={{ fontSize: 28 }} />}
          color={cyber.accent.green}
          loading={loading}
        />
        <StatCard
          title="Intelligence Sources"
          value={stats?.total_unique_sources || 0}
          icon={<RssFeedIcon sx={{ fontSize: 28 }} />}
          color={cyber.accent.cyan}
          loading={loading}
          subtitle="Unique source feeds"
        />
      </StatsContainer>

      {/* CVE KPI Cards */}
      <SectionHeader>
        <BugReportIcon color="error" sx={{ fontSize: 28 }} />
        <Typography variant="h5" fontWeight="bold">Vulnerability Intelligence (CVEs)</Typography>
      </SectionHeader>

      <StatsContainer>
        <StatCard 
          title="Total Unique CVEs" 
          value={stats?.total_cves || 0}
          icon={<BugReportIcon sx={{ fontSize: 28 }} />}
          color="#d32f2f"
          loading={loading}
        />
        <StatCard 
          title="CVEs (Last 7 Days)" 
          value={stats?.cves_last_7_days || 0}
          icon={<TrendingUpIcon sx={{ fontSize: 28 }} />}
          color="#f57c00"
          loading={loading}
        />
        <StatCard 
          title="Exploited in Wild" 
          value={stats?.exploited_in_wild_count || 0}
          icon={<WarningAmberIcon sx={{ fontSize: 28 }} />}
          color="#ff1744"
          loading={loading}
          subtitle="Active exploitation observed"
        />
        <StatCard 
          title="Avg. CVSS Score" 
          value={stats?.avg_cvss_score?.toFixed(1) || 'N/A'}
          icon={<ShieldIcon sx={{ fontSize: 28 }} />}
          color="#7b1fa2"
          loading={loading}
          subtitle="Across all CVEs"
        />
      </StatsContainer>

      <Divider sx={{ my: 4 }} />

      {/* Reports & IOCs Charts Section */}
      <SectionHeader>
        <AssessmentIcon color="primary" sx={{ fontSize: 28 }} />
        <Typography variant="h5" fontWeight="bold">Reports & IOCs Analytics</Typography>
      </SectionHeader>

      {loading ? (
        <ChartContainer>
          {[1, 2, 3].map((i) => (
            <ChartPaper key={i}>
              <Skeleton variant="text" width="60%" height={40} />
              <Skeleton variant="rectangular" height={350} sx={{ mt: 2 }} />
            </ChartPaper>
          ))}
        </ChartContainer>
      ) : (
        <ChartContainer>
          {/* Bar Chart - Report Sources */}
          <Fade in timeout={800}>
            <ChartPaper elevation={2}>
              <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DescriptionIcon color="primary" />
                Top Report Sources
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Most active intelligence sources
              </Typography>
              <ResponsiveContainer width="100%" height={350}>
                <BarChart
                  data={stats?.top_report_sources || []}
                  layout="vertical"
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <defs>
                    <linearGradient id="colorBar" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="5%" stopColor={cyber.primary} stopOpacity={0.8}/>
                      <stop offset="95%" stopColor={cyber.accent.cyan} stopOpacity={0.8}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.2)} />
                  <XAxis type="number" />
                  <YAxis dataKey="source" type="category" width={150} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" fill="url(#colorBar)" radius={[0, 8, 8, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartPaper>
          </Fade>
          
          {/* Pie Chart - Report Tags */}
          <Fade in timeout={1000}>
            <ChartPaper elevation={2}>
              <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AssessmentIcon color="primary" />
                Top Report Tags
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Most common threat categories
              </Typography>
              {stats?.top_tags && stats.top_tags.length > 0 ? (
                <ResponsiveContainer width="100%" height={350}>
                  <PieChart>
                    <Pie
                      data={stats.top_tags}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={renderCustomizedLabel}
                      outerRadius={120}
                      innerRadius={60}
                      fill="#8884d8"
                      dataKey="value"
                      nameKey="name"
                    >
                      {stats.top_tags.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                    <Legend 
                      verticalAlign="bottom" 
                      height={36}
                      formatter={(value) => <span style={{ fontSize: '12px' }}>{value}</span>}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 350 }}>
                  <Typography color="text.secondary">No tag data available</Typography>
                </Box>
              )}
            </ChartPaper>
          </Fade>

          {/* Pie Chart - IOC Distribution */}
          <Fade in timeout={1200}>
            <ChartPaper elevation={2}>
              <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SecurityIcon color="primary" />
                IOC Type Distribution
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Breakdown by indicator type
              </Typography>
              {stats?.ioc_type_distribution && stats.ioc_type_distribution.length > 0 ? (
                <ResponsiveContainer width="100%" height={350}>
                  <PieChart>
                    <Pie
                      data={stats.ioc_type_distribution}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={renderCustomizedLabel}
                      outerRadius={120}
                      innerRadius={60}
                      fill="#8884d8"
                      dataKey="value"
                      nameKey="name"
                    >
                      {stats.ioc_type_distribution.map((entry, index) => (
                        <Cell key={`ioc-cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                    <Legend 
                      verticalAlign="bottom" 
                      height={36}
                      formatter={(value) => <span style={{ fontSize: '12px' }}>{value}</span>}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 350 }}>
                  <Typography color="text.secondary">No IOC data available</Typography>
                </Box>
              )}
            </ChartPaper>
          </Fade>
        </ChartContainer>
      )}

      <Divider sx={{ my: 4 }} />

      {/* CVE Charts Section */}
      <SectionHeader>
        <BugReportIcon color="error" sx={{ fontSize: 28 }} />
        <Typography variant="h5" fontWeight="bold">CVE Analytics</Typography>
      </SectionHeader>

      {loading ? (
        <ChartContainer>
          {[1, 2, 3].map((i) => (
            <ChartPaper key={`cve-skeleton-${i}`}>
              <Skeleton variant="text" width="60%" height={40} />
              <Skeleton variant="rectangular" height={350} sx={{ mt: 2 }} />
            </ChartPaper>
          ))}
        </ChartContainer>
      ) : (
        <ChartContainer>
          {/* Pie Chart - CVE Severity Distribution */}
          <Fade in timeout={1400}>
            <ChartPaper elevation={2}>
              <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WarningAmberIcon sx={{ color: '#f57c00' }} />
                CVE Severity Distribution
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Vulnerabilities grouped by CVSS severity
              </Typography>
              {severityChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={350}>
                  <PieChart>
                    <Pie
                      data={severityChartData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={renderCustomizedLabel}
                      outerRadius={120}
                      innerRadius={60}
                      fill="#8884d8"
                      dataKey="value"
                      nameKey="name"
                    >
                      {severityChartData.map((entry, index) => (
                        <Cell 
                          key={`severity-cell-${index}`} 
                          fill={SEVERITY_COLORS[entry.name] || COLORS[index % COLORS.length]} 
                        />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                    <Legend 
                      verticalAlign="bottom" 
                      height={36}
                      formatter={(value) => (
                        <span style={{ 
                          fontSize: '12px', 
                          color: SEVERITY_COLORS[value as string] || 'inherit',
                          fontWeight: 'bold'
                        }}>
                          {value}
                        </span>
                      )}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 350 }}>
                  <Typography color="text.secondary">No CVE severity data available</Typography>
                </Box>
              )}
            </ChartPaper>
          </Fade>

          {/* Bar Chart - Top Mentioned CVEs */}
          <Fade in timeout={1600}>
            <ChartPaper elevation={2}>
              <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <BugReportIcon sx={{ color: '#d32f2f' }} />
                Top Mentioned CVEs
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Most frequently referenced vulnerabilities
              </Typography>
              {topCVEsChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart
                    data={topCVEsChartData}
                    layout="vertical"
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <defs>
                      <linearGradient id="colorCVEBar" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="5%" stopColor="#d32f2f" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#f57c00" stopOpacity={0.8}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.2)} />
                    <XAxis type="number" />
                    <YAxis 
                      dataKey="cve_id" 
                      type="category" 
                      width={140} 
                      tick={{ fontSize: 11 }}
                    />
                    <Tooltip content={<CVETooltip />} />
                    <Bar 
                      dataKey="mention_count" 
                      fill="url(#colorCVEBar)" 
                      radius={[0, 8, 8, 0]} 
                      name="Mentions"
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 350 }}>
                  <Typography color="text.secondary">No CVE mention data available</Typography>
                </Box>
              )}
            </ChartPaper>
          </Fade>

          {/* Bar Chart - Top Affected Vendors */}
          <Fade in timeout={1800}>
            <ChartPaper elevation={2}>
              <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ShieldIcon sx={{ color: '#7b1fa2' }} />
                Top Affected Vendors
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Vendors with most CVEs in our intelligence
              </Typography>
              {topVendorsChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart
                    data={topVendorsChartData}
                    layout="vertical"
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <defs>
                      <linearGradient id="colorVendorBar" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="5%" stopColor="#7b1fa2" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#9c27b0" stopOpacity={0.8}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.2)} />
                    <XAxis type="number" />
                    <YAxis 
                      dataKey="vendor" 
                      type="category" 
                      width={120}
                      tick={{ fontSize: 11 }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar 
                      dataKey="count" 
                      fill="url(#colorVendorBar)" 
                      radius={[0, 8, 8, 0]}
                      name="CVE Count" 
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 350 }}>
                  <Typography color="text.secondary">No vendor data available</Typography>
                </Box>
              )}
            </ChartPaper>
          </Fade>
        </ChartContainer>
      )}
    </Box>
  );
};

export default StatisticsPage;